import { createSlice, PayloadAction, createAction } from "@reduxjs/toolkit";
import { ActionType } from "../../types/actionType";
import { RootState } from "../store";
import { ErrorType } from "../../types/errorType";
import { API_ENDPOINTS } from "Config/apiConfig";
import {
  AppTypeWithData,
  initialState,
  createFetchInitialDataThunk,
  createFetchProgressiveDataThunk,
  ApiResponse,
} from "../../utils/fetchDataUtils";

// Initial state
const initialActionListState: AppTypeWithData<ActionType> = initialState<ActionType>();

// Action creators
export const appendDataAction = createAction<ActionType[]>("actionList/appendData");
export const setProgressAction = createAction<number>("actionList/setProgress");

const appendData = (state: AppTypeWithData<ActionType>, action: PayloadAction<ActionType[]>) => {
  const uniqueRecords = action.payload.filter(
    (newRecord) => !state.data.some((existingRecord) => existingRecord.id === newRecord.id)
  );
  state.data.push(...uniqueRecords); // Append only unique records
};

const setProgress = (state: AppTypeWithData<ActionType>, action: PayloadAction<number>) => {
  state.progress = action.payload;
};

// Thunks for fetching data
export const fetchInitialData = createFetchInitialDataThunk<ActionType>("action", API_ENDPOINTS.actions);

export const fetchProgressiveData = createFetchProgressiveDataThunk<ActionType>(
  "action",
  API_ENDPOINTS.actions,
  (state: RootState) => state.actionList.totalRecords,
  (state: RootState) => state.actionList.data.length,
  appendDataAction,
  setProgressAction
);

// Slice definition
const actionListSlice = createSlice({
  name: "actionList",
  initialState: initialActionListState,
  reducers: {
    appendData,
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    clearData: (state) => {
      state.data = []; //Reset the data array to an empty state
    },
    setProgress,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInitialData.fulfilled, (state, action: PayloadAction<ApiResponse<ActionType>>) => {
      state.data = action.payload.results;
      state.totalRecords = action.payload.totalRecords;
      state.totalPages = action.payload.totalPages;
      state.page = action.payload.page;
      state.loading = false;
      state.isInitialLoadComplete = true;
    });
    builder.addCase(fetchInitialData.rejected, (state, action) => {
      state.error = action.payload as ErrorType;
      state.loading = false;
    });
    builder.addCase(fetchProgressiveData.pending, (state) => {
      state.progressiveLoading = true;
      state.progress = 0;
      state.error = null;
    });
    builder.addCase(fetchProgressiveData.fulfilled, (state) => {
      state.progressiveLoading = false;
      state.progress = 100;
    });
    builder.addCase(fetchProgressiveData.rejected, (state, action) => {
      state.error = action.payload as ErrorType;
      state.progressiveLoading = false;
    });
  },
});

// Export actions and reducer
export const { setPageSize, setCurrentPage, clearData } = actionListSlice.actions;
export const actionRootState = (state: RootState) => state.actionList;
export default actionListSlice.reducer;
